import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  type OnChanges,
  type OnInit,
  output,
  type SimpleChanges,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { updateOrAdd } from '@core/helpers/helpers';
import type { Creditor } from '@core/models/creditor';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconComponent } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule } from '@fino-ui/translation';
import { CaptureTransactionService } from '@modules/capture-transaction/capture-transaction.service';
import { clone, cloneDeep, filter, map, merge, pick } from 'lodash';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CreditorSearchComponent } from './creditor-search/creditor-search.component';
import {
  DIRECT_DEBITS_FIELDS,
  isValidCreditorId,
} from './direct-debits.contants';

@Component({
  selector: 'app-direct-debits',
  templateUrl: './direct-debits.component.html',
  styleUrls: ['./direct-debits.component.sass'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FinoTranslationModule,
    FinoCommonModule,
    FinoInputModule,
    ButtonModule,
    CreditorSearchComponent,
    FinoIconComponent,
  ],
})
export class DirectDebitsComponent implements OnInit, OnChanges {
  public readonly captureTransactionService = inject(CaptureTransactionService);

  public readonly form = input<SubmittableForm>(new SubmittableForm({}));

  public readonly directDebits = input<any[]>([]);
  public readonly filteredDirectDebits = signal<any[]>([]);

  public searchForm: SubmittableForm = new SubmittableForm({
    search: new FormControl('', []),
  });

  readonly directDebitsSubmitted = output<any>();

  directDebitSelected = new EventEmitter<boolean>();

  public readonly isUpdateMode = signal(false);

  public model: any = {};

  public readonly toggleAllDelete = signal(true);
  public directDebitsFields = DIRECT_DEBITS_FIELDS;
  public readonly creditorIdMayInvalid = signal(false);
  public readonly isMaybeOneTimeDirectDebit = signal(false);

  nameTerm$ = new Subject<string>();

  ngOnInit() {
    this.setupFilterForm();
    this.setupCreditorValidation();
    this.setupForm();
  }

  submit() {
    this.form().markAllAsTouched();
    if (this.form().valid) {
      this.form().controls.creditorId.setValue(
        this.form().controls.creditorId.value.replace(/ /g, ''),
      );

      const toBeAdded = this.form().value;
      if (!toBeAdded.id) {
        toBeAdded.id = uuidv4();
      }
      this.isUpdateMode.set(false);
      updateOrAdd(
        this.directDebits(),
        merge(toBeAdded, { selected: true }),
        'id',
      );

      this.filteredDirectDebits.set(clone(this.directDebits()));
      this.searchForm.reset();

      this.directDebitsSubmitted.emit(this.form().value);
      this.form().reset();
    }
  }

  get hasName() {
    return this.form().controls.name?.value;
  }

  get hasCreditorId() {
    return this.form().controls.creditorId?.value;
  }

  readonly hasDirectDebits = computed(() => {
    const directDebits = this.directDebits();
    return directDebits && directDebits.length > 0;
  });

  get isFormValid() {
    return this.form().valid;
  }

  setupForm() {
    this.form().controls.purpose?.valueChanges.subscribe((purpose: string) => {
      this.isMaybeOneTimeDirectDebit.set(
        purpose?.toUpperCase().includes('ELV'),
      );
    });
  }

  deleteItem(item: any, selected: boolean = true) {
    item.selected = selected;
    this.directDebitsSubmitted.emit(this.form().value);
  }

  deleteAllItems() {
    this.toggleAllDelete.set(!this.toggleAllDelete());
    map(this.directDebits(), (d) => this.deleteItem(d, this.toggleAllDelete()));
  }

  duplicateItem(item: any) {
    const cloned = cloneDeep(item);
    cloned.id = uuidv4();
    this.form().patchValue(cloned);
  }

  editItem(item: any) {
    this.isUpdateMode.set(true);
    this.form().patchValue(pick(item, this.directDebitsFields));
  }

  handleCreditorResultSelected(creditor) {
    if (!creditor) return;

    this.form().patchValue({
      creditorId: creditor.creditorId,
      name: creditor.name,
      preferredContact: creditor.preferredContact,
      ignoreCreditor: creditor.ignoreCreditor,
    });

    if (this.isIgnorableCreditor(creditor)) {
      this.form().patchValue({ purpose: '-', mref: '-' });
      this.submit();
    } else {
      this.form().patchValue({ purpose: '', mref: '' });
    }
    this.directDebitSelected.next(true);
  }

  isIgnorableCreditor(creditor: Creditor) {
    return creditor.ignoreCreditor || creditor.preferredContact === 'online';
  }

  close() {
    this.directDebitSelected.next(true);
  }

  setupCreditorValidation() {
    this.form().controls.creditorId.valueChanges.subscribe((v) => {
      if (!v) return;
      v = v?.replace(/ /g, '');
      this.creditorIdMayInvalid.set(
        !this.form().untouched && !isValidCreditorId(v),
      );
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const { directDebits } = changes;
    if (directDebits) {
      this.filteredDirectDebits.set(clone(this.directDebits()));
    }
  }

  setupFilterForm() {
    this.searchForm.controls.search.valueChanges.subscribe(
      (keyword: string) => {
        if (!keyword) {
          this.filteredDirectDebits.set(clone(this.directDebits()));
          return;
        }
        this.filteredDirectDebits.set(
          filter(this.directDebits(), (item) =>
            item.name.toLowerCase().includes(keyword.toLowerCase()),
          ),
        );
      },
    );
  }
}
